import { inject, Injectable } from '@angular/core';

import { translate } from '@jsverse/transloco';
import {
  DateFormatOptions,
  NumberStyles,
  TranslocoLocaleService,
} from '@jsverse/transloco-locale';
import { ValueFormatterParams } from 'ag-grid-enterprise';

@Injectable({ providedIn: 'root' })
export class ColumnUtilsService {
  private readonly localeService = inject(TranslocoLocaleService);

  /**
   * Transform an ag-grid number value into the current locale's number format.
   * * @usageNotes
   *
   * ### options
   *
   * The value's decimal representation is specified by the `options`
   * parameter. The most common props are:
   *
   *  - `minimumIntegerDigits`:
   * The minimum number of integer digits before the decimal point.
   * Default is 1.
   *
   * - `minimumFractionDigits`:
   * The minimum number of digits after the decimal point.
   * Default is 0.
   *
   *  - `maximumFractionDigits`:
   * The maximum number of digits after the decimal point.
   * Default is 3.
   *
   * If the formatted value is truncated it will be rounded using the "to-nearest" method
   *
   * ### type
   *
   * default's to 'decimal' but can be used for i.e. percentages
   *
   * ### fallback
   *
   * flag for using a fallback value in case of undefined or empty string
   *
   * For further information on number formatters see
   * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/NumberFormat/NumberFormat
   *
   * For further information on ag grid value formatters see
   * https://www.ag-grid.com/angular-data-grid/value-formatters
   */
  formatNumber = (
    params: ValueFormatterParams,
    options?: Intl.NumberFormatOptions,
    type: NumberStyles = 'decimal',
    fallback?: boolean
  ): string => {
    const result = this.localeService.localizeNumber(
      params.value,
      type,
      undefined,
      options
    );

    if ((result === undefined || result === '') && fallback) {
      return translate('shared.undefinedAttribute');
    }

    return result;
  };

  /**
   * Transform an ag-grid date value into the current locale's date format.
   *
   * * @usageNotes
   *
   * ### date
   * date can be a `Date` object, a number (milliseconds since UTC epoch),
   * or an ISO string (https://www.w3.org/TR/NOTE-datetime).
   *
   * ### options
   *
   * Supports Intl calendar types
   *
   * ### fallback
   *
   * flag for using a fallback value in case of undefined or empty string
   *
   * For further information on ag grid value formatters see
   * https://www.ag-grid.com/angular-data-grid/value-formatters
   */
  formatDate = (
    params: ValueFormatterParams,
    options?: DateFormatOptions,
    fallback?: boolean
  ): string => {
    if (params.value) {
      return this.localeService.localizeDate(params.value, undefined, options);
    } else {
      if (fallback) {
        return translate('shared.undefinedAttribute');
      }

      return '';
    }
  };
}
